import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../components/Alert";
import api from "../globals/axios";

export const usePersonalInformation = ({ id = null } = {}) => {
  const [profileData, setProfileData] = useState({
    full_name: "",
    phone_number: "",
    alternate_phone_number: "",
    village: "",
    address_line1: "",
    address_line2: "",
    pin_code: "",
    state: "",
    state_details: { id: "", name: "" },
    district: "",
    district_details: { id: "", name: "" },
    postoffice: "",
    postoffice_details: { id: "", name: "" },
    profile_picture: null,
    area_manager: "",
    vle: "",
    kyc_details: {
      aadhar_number: "",
      pan_number: "",
      aadhar_image: null,
      pan_image: null,
    },
  });
  const [postOffices, setPostOffices] = useState([]);
  const [pincodeError, setPincodeError] = useState("");
  const [loading, setLoading] = useState(false);
  const [farmerId, setFarmerId] = useState("");
  const [userId, setUserId] = useState(null);
  const [modifiedFields, setModifiedFields] = useState(new Set());
  const [previews, setPreviews] = useState({
    profile_picture: null,
    aadhar_image: null,
    pan_image: null,
  });

  const navigate = useNavigate();
  const { showAlert } = useAlert();

  useEffect(() => {
    if (id) {
      loadDetails();
    }
  }, [id]);

  const loadDetails = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/auth/profile?id=${id}`);
      const userData = res.data.data[0];

      if (userData) {
        setFarmerId(userData.user.custom_id || "");
        setUserId(userData.id || null);

        setPreviews({
          profile_picture: userData.profile_picture || null,
          aadhar_image: userData.kyc_details?.aadhar_image || null,
          pan_image: userData.kyc_details?.pan_image || null,
        });

        setProfileData({
          full_name: userData.user.full_name || "",
          phone_number: userData.user.phone_number || "",
          alternate_phone_number: userData.alternate_phone_number || "",
          village: userData.village || "",
          address_line1: userData.address_line1 || "",
          address_line2: userData.address_line2 || "",
          pin_code: userData.pin_code || "",
          state: userData.state_details?.name || "",
          state_details: userData.state_details || {},
          district: userData.district_details?.name || "",
          district_details: userData.district_details || {},
          postoffice: userData.postoffice_details?.name || "",
          postoffice_details: userData.postoffice_details || {},
          profile_picture: userData.profile_picture || null,
          kyc_details: {
            aadhar_number: userData.kyc_details?.aadhar_number || "",
            pan_number: userData.kyc_details?.pan_number || "",
            aadhar_image: userData.kyc_details?.aadhar_image || null,
            pan_image: userData.kyc_details?.pan_image || null,
          },
        });

        // Fetch and set post offices based on the pin code
        if (userData.pin_code?.length === 6) {
          const response = await api.get(`common/pincode/${userData.pin_code}`);
          const postOfficesData = response.data[0]?.PostOffice || [];
          setPostOffices(postOfficesData);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setModifiedFields((prev) => new Set([...prev, name]));

    if (name.includes("kyc_details.")) {
      const key = name.split(".")[1];
      setProfileData((prevData) => ({
        ...prevData,
        kyc_details: {
          ...prevData.kyc_details,
          [key]: value,
        },
      }));
    } else if (name === "pin_code") {
      setProfileData((prevData) => ({ ...prevData, [name]: value }));
      if (value.length === 6) {
        fetchAddressDetails(value);
      }
    } else {
      setProfileData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const fetchAddressDetails = async (pincode) => {
    try {
      const response = await api.get(`common/pincode/${pincode}`);
      const postOfficesData = response.data[0]?.PostOffice || [];
      if (postOfficesData.length > 0) {
        const firstPostOffice = postOfficesData[0];
        setPostOffices(postOfficesData);

        // Update state, district and postoffice with details
        setModifiedFields(
          (prev) => new Set([...prev, "state", "district", "postoffice"])
        );

        setProfileData((prevData) => ({
          ...prevData,
          state: firstPostOffice.State,
          state_details: {
            ...prevData.state_details,
            name: firstPostOffice.State,
          },
          district: firstPostOffice.District,
          district_details: {
            ...prevData.district_details,
            name: firstPostOffice.District,
          },
          postoffice: firstPostOffice.Name,
          postoffice_details: {
            ...prevData.postoffice_details,
            name: firstPostOffice.Name,
          },
        }));
        setPincodeError("");
      } else {
        setPincodeError("Invalid Pincode");
        setPostOffices([]);
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
      setPincodeError("Error fetching address details");
      setPostOffices([]);
    }
  };

  const handleClick = async (filteredData) => {
    if (pincodeError) {
      showAlert("error", pincodeError);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      const appendFile = (key, file, prefix) => {
        if (file instanceof File) {
          const fileExtension = file.name.split(".").pop();
          const renamedFile = new File(
            [file],
            `${prefix}_${key}.${fileExtension}`,
            { type: file.type }
          );
          formData.append(key, renamedFile);
        }
      };

      if (userId) {
        modifiedFields.forEach((field) => {
          if (field === "profile_picture") {
            appendFile(
              field,
              profileData.profile_picture,
              profileData.phone_number
            );
          } else if (field.startsWith("kyc_details.")) {
            const key = field.split(".")[1];
            if (key === "aadhar_image" || key === "pan_image") {
              appendFile(
                `kyc_details.${key}`,
                profileData.kyc_details[key],
                profileData.phone_number
              );
            } else if (profileData.kyc_details[key]) {
              formData.append(
                `kyc_details.${key}`,
                profileData.kyc_details[key]
              );
            }
          } else if (profileData[field]) {
            formData.append(field, profileData[field]);
          }
        });
        await api.patch(`/auth/profile/${userId}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showAlert("success", "Profile Updated Successfully");
      } else {
        Object.entries(filteredData).forEach(([key, value]) => {
          if (key === "kyc_details") {
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subKey === "aadhar_image" || subKey === "pan_image") {
                appendFile(
                  `kyc_details.${subKey}`,
                  subValue,
                  filteredData.phone_number
                );
              } else {
                formData.append(`kyc_details.${subKey}`, subValue);
              }
            });
          } else if (key === "profile_picture") {
            appendFile(key, value, filteredData.phone_number);
          } else if (
            [
              "state_details",
              "district_details",
              "postoffice_details",
            ].includes(key)
          ) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        });
        const url = !id ? "/usermanagement/add-farmer/" : "/auth/profile/";
        const response = await api.post(url, formData);
        showAlert("success", response.data.message);
        return response.data;
      }
      navigate(-1);
    } catch (error) {
      console.error("Error while creating/updating profile:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event, field) => {
    let file = event.target.files[0];
    if (!file) {
      showAlert("error", "Please select a file");
      return;
    }

    file = new File([file], file.name, { type: file.type });

    if (field === "aadhar_image" || field === "pan_image") {
      setModifiedFields((prev) => new Set([...prev, `kyc_details.${field}`]));
    } else {
      setModifiedFields((prev) => new Set([...prev, field]));
    }

    const previewUrl = URL.createObjectURL(file);
    setPreviews((prev) => ({
      ...prev,
      [field]: previewUrl,
    }));

    if (field === "profile_picture") {
      setProfileData((prev) => ({
        ...prev,
        profile_picture: file,
      }));
    } else {
      setProfileData((prev) => ({
        ...prev,
        kyc_details: {
          ...prev.kyc_details,
          [field]: file,
        },
      }));
    }
  };

  return {
    profileData,
    setProfileData,
    postOffices,
    pincodeError,
    loading,
    farmerId,
    userId,
    modifiedFields,
    previews,
    setPreviews,
    handleChange,
    handleClick,
    handleImageUpload,
    fetchAddressDetails,
  };
};
